import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDataSubscription } from './queryKeys';
import { type MutateNotificationPolicyData } from './types';

type NotificationPolicyResponse = {
  code: number;
  id: UUID;
  message: string;
};

/**
 * Creates a notification policy.
 *
 * @param {MutateNotificationPolicyData} notificationPolicyData - An object containing the notification policy details to be created.
 * @returns {Promise<NotificationPolicyResponse>} - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription/operation/create_notification_policy_data_subscription_notification_policy_post
 */
export const createNotificationPolicy = async (
  notificationPolicyData: MutateNotificationPolicyData,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.DATA_SUBSCRIPTION.CREATE_NOTIFICATION_POLICY(), {
      json: notificationPolicyData,
    })
    .json<NotificationPolicyResponse>();

  return response;
};

/**
 * Custom hook for creating a notification policy using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationCreateNotificationPolicy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationPolicyData: MutateNotificationPolicyData) =>
      createNotificationPolicy(notificationPolicyData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeysDataSubscription.getAllNotificationPolicies(),
      });
    },
  });
};
